import { createFileRoute } from "@tanstack/react-router";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { VStack, HStack } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { useMcpSupportedProfiles } from "../../../components/custom/onboarding/useMcpSupportedProfiles";
import { Button } from "../../../components/core/basic/Button";
import { Tabs, TabList, Tab, TabPanel } from "../../../components/core/basic/Tabs";
import type { Organization } from "../../../zodios/common/orgSchema";

const McpTest: React.FC = () => {
  const { t } = useTranslation(["mcp", "common"]);
  const { trellisProfilesData, isLoading } = useMcpSupportedProfiles();
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState("tokens");

  const handleCopyToken = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (err) {
      // oxlint-disable-next-line no-console
      console.error("Failed to copy token:", err);
    }
  };

  return (
    <VStack gap={4} width="100%" maxWidth="1400px" margin="0 auto" padding="24px">
      {/* <h1 className={css({ color: "#333", fontSize: "xxlarge", fontWeight: "bold" })}>Trellis MCP Server</h1> */}

      <div
        className={css({
          border: "1px solid",
          borderColor: "trellisPalette.11/30",
          borderRadius: "light",
          width: "100%",
          padding: "large",
          backgroundColor: "trellisPalette.11/15",
        })}
      >
        <VStack gap="small">
          <h2 className={css({ color: "trellisPalette.3", fontSize: "large", fontWeight: "600" })}>
            {t(($) => $.mcp["MCP Server URL"])}
          </h2>
          <HStack gap="small" alignItems="center">
            <code
              className={css({
                flex: "1",
                border: "1px solid #e5e7eb",
                borderRadius: "4px",
                padding: "small",
                color: "#1f2937",
                fontFamily: "monospace",
                fontSize: "medium",
                backgroundColor: "white",
              })}
            >
              https://api.growthvectors.io/mcp/
            </code>
            <Button
              size="small"
              variant="secondary"
              onPress={async () => await navigator.clipboard.writeText("https://api.growthvectors.io/mcp/")}
            >
              {t(($) => $.mcp["Copy URL"])}
            </Button>
          </HStack>
          <p className={css({ marginTop: "xsmall", color: "#6b7280", fontSize: "small" })}>
            {t(($) => $.mcp["Use this base URL with your profile's MCP token to access the MCP API endpoints."])}
          </p>
        </VStack>
      </div>

      <div
        className={css({
          borderRadius: "8px",
          width: "100%",
          padding: "24px",
          backgroundColor: "white",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        })}
      >
        <Tabs selectedKey={selectedTab} onSelectionChange={(key) => setSelectedTab(key as string)}>
          <TabList>
            <Tab id="tokens">{t(($) => $.mcp.tabs["Tokens"])}</Tab>
            <Tab id="usage">{t(($) => $.mcp.tabs["Usage Examples"])}</Tab>
          </TabList>
          <TabPanel id="tokens">
            <h2 className={css({ marginBottom: "medium", fontSize: "large", fontWeight: "600" })}>
              {t(($) => $.mcp["Profile Tokens"])}
            </h2>
            {isLoading ?
              <div className={css({ padding: "20px", color: "#666", textAlign: "center" })}>
                {t(($) => $.common["Loading..."])}
              </div>
            : <div className={css({ width: "100%", overflowX: "auto" })}>
                <table
                  className={css({
                    width: "100%",
                    fontSize: "medium",
                    borderCollapse: "collapse",
                    "& th": {
                      borderColor: "lightgrey",
                      borderBottom: "1px solid",
                      paddingY: "small",
                      paddingX: "medium",
                      color: "grey",
                      fontSize: "small",
                      fontWeight: "400",
                      textAlign: "left",
                      textTransform: "uppercase",
                    },
                    "& td": {
                      borderColor: "lightgrey",
                      borderBottom: "1px solid",
                      paddingY: "medium",
                      paddingX: "medium",
                    },
                    "& tbody tr:hover": {
                      backgroundColor: "#f9f9f9",
                    },
                  })}
                >
                  <thead>
                    <tr>
                      <th>{t(($) => $.mcp.table["Name"])}</th>
                      <th>{t(($) => $.mcp.table["Marketplaces"])}</th>
                      <th>{t(($) => $.mcp.table["MCP Token"])}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trellisProfilesData.map((profile: Organization) => (
                      <tr key={profile.id}>
                        <td className={css({ fontWeight: "500" })}>
                          {profile.org_type === "WALMART" ?
                            t(($) => $.mcp.table["{name} (Walmart)"], { name: profile.name })
                          : profile.name}
                        </td>
                        <td>
                          <div className={css({ display: "flex", gap: "xsmall", flexWrap: "wrap" })}>
                            {profile.marketplaces.map((marketplace: string) => (
                              <span
                                key={marketplace}
                                className={css({
                                  borderRadius: "4px",
                                  paddingY: "xsmall",
                                  paddingX: "small",
                                  color: "#3730a3",
                                  fontSize: "small",
                                  fontWeight: "500",
                                  backgroundColor: "#e0e7ff",
                                })}
                              >
                                {marketplace}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <HStack gap="small" alignItems="center">
                            <code
                              className={css({
                                borderRadius: "4px",
                                padding: "xsmall",
                                fontFamily: "monospace",
                                fontSize: "small",
                                backgroundColor: "#f5f5f5",
                              })}
                            >
                              {profile.mcp_token}
                            </code>
                            <Button
                              size="small"
                              variant="secondary"
                              onPress={async () => profile.mcp_token && (await handleCopyToken(profile.mcp_token))}
                            >
                              {copiedToken === profile.mcp_token ? t(($) => $.mcp["Copied!"]) : t(($) => $.mcp["Copy"])}
                            </Button>
                          </HStack>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            }
          </TabPanel>
          <TabPanel id="usage">
            <VStack gap="large" alignItems="flex-start">
              <div>
                <h2 className={css({ marginBottom: "small", fontSize: "large", fontWeight: "600", textAlign: "left" })}>
                  {t(($) => $.mcp["Authentication Methods"])}
                </h2>
                <p
                  className={css({
                    marginBottom: 0,
                    paddingBottom: 0,
                    color: "grey",
                    fontSize: "medium",
                    textAlign: "left",
                  })}
                >
                  {t(
                    ($) =>
                      $.mcp[
                        "There are two ways to authenticate your requests to the Trellis MCP API. Choose the method that works best for your use case."
                      ],
                  )}
                </p>
              </div>

              <VStack gap="medium" alignItems="flex-start" width="100%">
                <h3
                  className={css({
                    color: "trellisPalette.3",
                    fontSize: "medium",
                    fontWeight: "600",
                    textAlign: "left",
                  })}
                >
                  {t(($) => $.mcp["Method 1: Authorization Bearer Token (HTTP Header)"])}
                </h3>
                <p className={css({ color: "grey", fontSize: "small", textAlign: "left" })}>
                  {t(
                    ($) =>
                      $.mcp[
                        "Add your token as a Bearer token in the Authorization header. This is the recommended method for applications and scripts."
                      ],
                  )}
                </p>
                <div
                  className={css({
                    border: "1px solid",
                    borderColor: "lightgrey",
                    borderRadius: "light",
                    padding: "medium",
                    backgroundColor: "lightgrey/30",
                  })}
                >
                  <pre
                    className={css({
                      margin: "0",
                      color: "darkgrey",
                      fontFamily: "monospace",
                      fontSize: "small",
                      textAlign: "left",
                      wordBreak: "break-all",
                      whiteSpace: "pre-wrap",
                    })}
                  >
                    {`curl -X GET "https://api.growthvectors.io/mcp/" \\
  -H "Authorization: Bearer YOUR_MCP_TOKEN"`}
                  </pre>
                </div>

                <div
                  className={css({
                    border: "1px solid",
                    borderColor: "trellisPalette.11/30",
                    borderRadius: "light",
                    padding: "medium",
                    backgroundColor: "trellisPalette.11/15",
                  })}
                >
                  <p className={css({ margin: "0", color: "trellisPalette.3", fontSize: "small" })}>
                    <strong>{t(($) => $.mcp["💡 Best for:"])}</strong>{" "}
                    {t(($) => $.mcp["Server-side applications, API clients, and custom integrations"])}
                  </p>
                </div>
              </VStack>

              <VStack gap="medium" alignItems="flex-start" width="100%">
                <h3
                  className={css({
                    color: "trellisPalette.3",
                    fontSize: "medium",
                    fontWeight: "600",
                    textAlign: "left",
                  })}
                >
                  {t(($) => $.mcp["Method 2: Query Parameter"])}
                </h3>
                <p className={css({ color: "grey", fontSize: "small", textAlign: "left" })}>
                  {t(
                    ($) =>
                      $.mcp[
                        "Pass your token as a query parameter. This method is useful for testing or when using AI assistants like ChatGPT or Claude."
                      ],
                  )}
                </p>
                <div
                  className={css({
                    border: "1px solid",
                    borderColor: "lightgrey",
                    borderRadius: "light",
                    padding: "medium",
                    backgroundColor: "lightgrey/30",
                  })}
                >
                  <pre
                    className={css({
                      margin: "0",
                      color: "darkgrey",
                      fontFamily: "monospace",
                      fontSize: "small",
                      textAlign: "left",
                      wordBreak: "break-all",
                      whiteSpace: "pre-wrap",
                    })}
                  >
                    {`https://api.growthvectors.io/mcp/?api_key=YOUR_MCP_TOKEN`}
                  </pre>
                </div>

                <div
                  className={css({
                    border: "1px solid",
                    borderColor: "trellisPalette.11/30",
                    borderRadius: "light",
                    padding: "medium",
                    backgroundColor: "trellisPalette.11/15",
                  })}
                >
                  <p className={css({ margin: "0", color: "trellisPalette.3", fontSize: "small" })}>
                    <strong>{t(($) => $.mcp["💡 Best for:"])}</strong>{" "}
                    {t(($) => $.mcp["AI assistants (ChatGPT, Claude), browser-based tools, and quick testing"])}
                  </p>
                </div>
              </VStack>

              <VStack gap="medium" alignItems="flex-start" width="100%">
                <h3
                  className={css({
                    color: "trellisPalette.13",
                    fontSize: "medium",
                    fontWeight: "600",
                    textAlign: "left",
                  })}
                >
                  {t(($) => $.mcp["Using with AI Assistants"])}
                </h3>
                <p className={css({ color: "grey", fontSize: "small", textAlign: "left" })}>
                  {t(
                    ($) =>
                      $.mcp[
                        "You can configure AI assistants like ChatGPT or Claude to use your MCP token by providing them with the server URL and your token as a query parameter."
                      ],
                  )}
                </p>
                <div
                  className={css({
                    border: "1px solid",
                    borderColor: "trellisPalette.1/30",
                    borderRadius: "light",
                    padding: "medium",
                    backgroundColor: "trellisPalette.1/15",
                  })}
                >
                  <p
                    className={css({
                      margin: "0",
                      marginBottom: "small",
                      color: "trellisPalette.13",
                      fontSize: "small",
                    })}
                  >
                    <strong>{t(($) => $.mcp["Example configuration:"])}</strong>
                  </p>
                  <pre
                    className={css({
                      margin: "0",
                      color: "darkgrey",
                      fontFamily: "monospace",
                      fontSize: "small",
                      textAlign: "left",
                      wordBreak: "break-all",
                      whiteSpace: "pre-wrap",
                    })}
                  >
                    {`Server URL: https://api.growthvectors.io/mcp/?api_key=YOUR_MCP_TOKEN

Replace YOUR_MCP_TOKEN with your profile token from the table above.`}
                  </pre>
                </div>
              </VStack>
            </VStack>
          </TabPanel>
        </Tabs>
      </div>
    </VStack>
  );
};

export const Route = createFileRoute("/_authenticated/user/mcp")({
  component: McpTest,
  staticData: { application: "Admin" },
});
